import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSellerListings } from "../hooks/useListings";
import { apiClient } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { Money } from "../components/Money";
import { ListingStatusBadge } from "../components/StatusBadge";
import { EmptyState, ErrorBanner, LoadingState, describeError } from "../components/States";

export function SellerDashboardPage() {
  const { data, isLoading, isError, error, refetch } = useSellerListings(true);
  const queryClient = useQueryClient();
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const deleteListing = useMutation({
    mutationFn: (id: string) => apiClient.deleteListing(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.sellerListings }),
    onError: (err) => setDeleteError(describeError(err)),
  });

  function handleDelete(id: string, title: string) {
    setDeleteError(null);
    if (window.confirm(`Permanently delete "${title}"? This can't be undone.`)) {
      deleteListing.mutate(id);
    }
  }

  return (
    <div className="stack">
      <div className="row row--between">
        <h1>Seller dashboard</h1>
        <div className="row">
          <Link to="/sell/analytics" className="btn btn-secondary">
            Analytics
          </Link>
          <Link to="/sell/orders" className="btn btn-secondary">
            My sales
          </Link>
          <Link to="/sell/offers" className="btn btn-secondary">
            Offers
          </Link>
          <Link to="/sell/new" className="btn btn-primary">
            New listing
          </Link>
        </div>
      </div>

      {isLoading && <LoadingState label="Loading your listings…" />}
      {isError && <ErrorBanner error={error} onRetry={() => void refetch()} />}
      {deleteError && (
        <p className="field-error-text" role="alert">
          {deleteError}
        </p>
      )}

      {data && data.items.length === 0 && (
        <EmptyState title="No listings yet">
          <Link to="/sell/new" className="btn btn-primary">
            Create your first listing
          </Link>
        </EmptyState>
      )}

      {data && data.items.length > 0 && (
        <div className="table-wrap card">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Price</th>
                <th>Status</th>
                <th>
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((listing) => (
                <tr key={listing.id}>
                  <td>{listing.title}</td>
                  <td>
                    <Money baseUnits={listing.priceBaseUnits} />
                  </td>
                  <td>
                    <ListingStatusBadge status={listing.status} />
                  </td>
                  <td>
                    <div className="row">
                      <Link to={`/sell/${listing.id}/edit`} className="btn btn-secondary btn-sm">
                        Manage
                      </Link>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        disabled={deleteListing.isPending}
                        onClick={() => handleDelete(listing.id, listing.title)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
